import React, { useState } from 'react';
import { useUser } from '../UserContext';
import { adminServices } from '../services/adminServices';

const AuditorProfile = ({ onboardingMode = false, targetStaff = null, onActionComplete = null }) => {
  const { user: currentUser, staffRecord: currentStaffRecord } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const isReviewMode = !!targetStaff;
  const record = isReviewMode ? targetStaff : currentStaffRecord;
  const uid = isReviewMode ? targetStaff.id : currentUser.uid;

  const [formData, setFormData] = useState({
    bio: record?.pendingUpdate?.bio || record?.profile?.bio || "",
    qualifications: record?.pendingUpdate?.qualifications || record?.profile?.qualifications || "",
    experience: record?.pendingUpdate?.experience || record?.profile?.experience || ""
  });

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setIsSaving(true);
    setSuccess(false);
    try {
      if (isReviewMode) {
        // Admin overrides directly to live profile
        await adminServices.approveProfileUpdate(uid, formData, currentUser.displayName);
      } else {
        // User submits for review
        await adminServices.updateProfile(uid, formData, currentUser.displayName);
      }
      setSuccess(true);
      if (onActionComplete) onActionComplete();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert("Error saving profile: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Reject these profile changes?")) return;
    setIsSaving(true);
    try {
      await adminServices.rejectProfileUpdate(uid, currentUser.displayName);
      if (onActionComplete) onActionComplete();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="auditor-profile-container" style={{ maxWidth: '800px' }}>
      <header style={{ marginBottom: '30px' }}>
        <h2 style={{ color: 'white', margin: 0 }}>My Professional Identity</h2>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
          This information is included in the "Certification & Sign-Off" section of reports you draft.
        </p>
      </header>

      {record?.pendingUpdate && !onboardingMode && (
        <div className="admin-banner-warning" style={{ background: 'rgba(209, 29, 126, 0.1)', border: '1px solid #d11d7e', color: '#d11d7e', padding: '15px', borderRadius: '6px', marginBottom: '20px', fontSize: '0.85rem' }}>
          <strong>AWAITING APPROVAL:</strong> {isReviewMode ? "This auditor has" : "You have"} pending changes. An Admin must verify these updates before they appear on official reports.
        </div>
      )}

      <div className="profile-editor-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '30px' }}>
        
        <form onSubmit={handleSave} className="editor-card" style={{ background: '#1e293b', padding: '25px', borderRadius: '8px' }}>
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#138a8a', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>Full Professional Name</label>
            <input 
              type="text" 
              value={isReviewMode ? targetStaff.displayName : currentUser.displayName} 
              disabled 
              style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid #334155', color: '#666', padding: '10px', borderRadius: '4px' }}
            />
            <small style={{ color: '#666', fontSize: '0.65rem' }}>Managed via Google Workspace</small>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#138a8a', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>Key Qualifications</label>
            <input 
              type="text" 
              placeholder="e.g. M.Ed Inclusion, Lead Accreditor ISO/IEC"
              value={formData.qualifications}
              onChange={(e) => setFormData({...formData, qualifications: e.target.value})}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '10px', borderRadius: '4px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#138a8a', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>Experience Summary</label>
            <input 
              type="text" 
              placeholder="e.g. 12 Years International Education Leadership"
              value={formData.experience}
              onChange={(e) => setFormData({...formData, experience: e.target.value})}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '10px', borderRadius: '4px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', color: '#138a8a', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '8px', textTransform: 'uppercase' }}>Professional Bio</label>
            <textarea 
              placeholder="One sentence maximum. e.g. 'Special Educator with 25 years of educational leadership experience' or 'Vice Principal of large international school' "
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              style={{ width: '100%', minHeight: '120px', background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '10px', borderRadius: '4px', lineHeight: '1.5' }}
            />
          </div>

          {isReviewMode ? (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" onClick={handleReject} className="btn-secondary" style={{ flex: 1 }}>Reject Changes</button>
              <button type="submit" className="btn-activate" disabled={isSaving} style={{ flex: 2 }}>
                {isSaving ? "Saving..." : "Approve & Save Live"}
              </button>
            </div>
          ) : (
            <button type="submit" className="btn-activate" disabled={isSaving} style={{ width: '100%' }}>
              {isSaving ? "Syncing..." : success ? "✓ Request Submitted" : "Submit Updates for Approval"}
            </button>
          )}
        </form>

        <aside className="profile-preview">
          <h4 style={{ color: '#94a3b8', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '15px' }}>
            {isReviewMode ? `Current Live ID: ${targetStaff.displayName}` : "Current Live ID"}
          </h4>
          <div style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px' }}>
             <div style={{ color: 'white', fontWeight: 'bold', marginBottom: '4px' }}>{isReviewMode ? targetStaff.displayName : currentUser.displayName}</div>
             <div style={{ color: '#138a8a', fontSize: '0.75rem', marginBottom: '10px' }}>{record?.profile?.qualifications || "No qualifications set"}</div>
             <div style={{ color: '#94a3b8', fontSize: '0.7rem', fontStyle: 'italic', lineHeight: '1.4' }}>
                {record?.profile?.bio || "No professional biography has been approved yet."}
             </div>
          </div>
          
          <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(19, 138, 138, 0.05)', borderRadius: '6px' }}>
            <p style={{ fontSize: '0.65rem', color: '#138a8a', margin: 0, lineHeight: '1.4' }}>
              <strong>Note:</strong> Changes to your profile will be reviewed by the System Owner to ensure consistency in IFIP accreditation reports.
            </p>
          </div>
        </aside>

      </div>
    </div>
  );
};

export default AuditorProfile;